/** Create and edit account records. */
import * as React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangleIcon, Loader2Icon } from 'lucide-react';
import { useAuth } from '@/components/auth/auth-provider';
import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { AccountBusinessLookup } from './account-business-lookup';
import { AccountOrganisationReview } from './account-organisation-review';
import { useGuardedNavigate } from '@/hooks/use-draft-navigation-guard';
import {
  getListAccountsQueryKey,
  useListAccounts,
  type Account,
  type AccountCreate,
  type AccountKind,
  type AccountRelationshipType,
  type ConsentStatus,
  type ReviewTenderPreparationAccountBody
} from '@cdf/api-client-react';

import { Button } from '@/components/ui/button';
import { FormFeedback, useRetainedForm, type FormIssue } from '@/components/interaction/form-feedback';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TestDataStatusControl } from '@/components/crm/test-data-controls';
import { ACCOUNT_RELATIONSHIP_OPTIONS } from '@/components/crm/account-relationship';

const DUPLICATE_CHECK_MIN_LENGTH = 3;

function normalizeAccountName(value: string) {
  return value.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

export type AccountDocumentPrefill = Partial<ReviewTenderPreparationAccountBody> & { name: string };
export type AccountFormDraft = { ownerKey: string; values: AccountCreate; source?: string; location: string; dirty: boolean };
interface AccountFormBaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: Account;
  prefill?: AccountDocumentPrefill;
  /** Plain source label, retained with the initialized draft; never included in web terms. */
  prefillSource?: string;
  defaultRelationshipType?: AccountRelationshipType;
  defaultAccountKind?: AccountKind;
  pending: boolean;
  onDraftChange?: (dirty: boolean) => void;
  /** Use the enclosing drawer's focus trap, with this component as its only active form. */
  presentation?: 'dialog' | 'embedded';
  draft?: React.RefObject<AccountFormDraft | undefined>;
  onPendingChange?: (pending: boolean) => void;
}
export type AccountFormProps = AccountFormBaseProps & (
  { submission?: undefined; onSubmit: (values: AccountCreate) => Promise<void> } |
  { submission: { kind: 'preparation'; preparationId: string; onReview: (values: ReviewTenderPreparationAccountBody) => Promise<void> }; onSubmit?: never }
);

/** Scope changes replace the draft owner; ordinary close/reopen retains the same owner. */
export function AccountFormDialog(props: AccountFormProps) {
  const access = useTenantEntity();
  const auth = useAuth();
  const ownerKey = JSON.stringify([auth.account?.stableId ?? '', access.selected?.tenantId, access.selected?.entityId]);
  return <ScopedAccountFormDialog key={ownerKey + ':' + (props.initial?.id ?? props.submission?.preparationId ?? 'new')} {...props}
    ownerKey={ownerKey} tenantId={access.selected?.tenantId} entityId={access.selected?.entityId}
    canSave={access.status === 'ready' && access.hasPermission('entity.records.write')}
    canLookup={access.status === 'ready' && access.hasPermission('entity.records.write') && access.hasPermission('entity.ai.use')} />;
}

/** One field/state owner shared by canonical create/edit and exact preparation review adapters. */
function ScopedAccountFormDialog({
  open,
  onOpenChange,
  initial,
  prefill,
  prefillSource,
  submission,
  ownerKey,
  tenantId,
  entityId,
  canSave,
  canLookup,
  defaultRelationshipType = 'prospect',
  defaultAccountKind = 'organization',
  onSubmit,
  pending,
  onDraftChange,
  presentation = 'dialog',
  draft,
  onPendingChange
}: AccountFormProps & { ownerKey: string; tenantId?: string; entityId?: string; canSave: boolean; canLookup: boolean }) {
  const formState = useRetainedForm(pending, onOpenChange);
  const initialized = React.useRef<string | null>(null);
  const snapshotReady = React.useRef(false);
  const returnFocus = React.useRef<HTMLElement | null>(null);
  const navigate = useGuardedNavigate();
  React.useEffect(() => { onDraftChange?.(formState.dirty); }, [formState.dirty, onDraftChange]);
  React.useEffect(() => { onPendingChange?.(formState.pending); }, [formState.pending, onPendingChange]);
  const [name, setName] = React.useState('');
  const [accountKind, setAccountKind] = React.useState<AccountKind>(defaultAccountKind);
  const [relationshipType, setRelationshipType] = React.useState<AccountRelationshipType>(defaultRelationshipType);
  const [website, setWebsite] = React.useState('');
  const [industry, setIndustry] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [emailDomain, setEmailDomain] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [ownerName, setOwnerName] = React.useState('');
  const [addressLine1, setAddressLine1] = React.useState('');
  const [city, setCity] = React.useState('');
  const [stateProvince, setStateProvince] = React.useState('');
  const [postcode, setPostcode] = React.useState('');
  const [country, setCountry] = React.useState('');
  const [annualRevenue, setAnnualRevenue] = React.useState('');
  const [numberOfEmployees, setNumberOfEmployees] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [emailConsent, setEmailConsent] = React.useState<ConsentStatus>('unknown');
  const [smsConsent, setSmsConsent] = React.useState<ConsentStatus>('unknown');
  const [consentSource, setConsentSource] = React.useState('');
  const [investorType, setInvestorType] = React.useState('');
  const [investmentFocus, setInvestmentFocus] = React.useState('');
  const [typicalChequeMin, setTypicalChequeMin] = React.useState('');
  const [typicalChequeMax, setTypicalChequeMax] = React.useState('');
  const [accreditationStatus, setAccreditationStatus] = React.useState('');
  const [isTestData, setIsTestData] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [fieldIssues, setFieldIssues] = React.useState<FormIssue[]>([]);
  const [debouncedName, setDebouncedName] = React.useState('');
  const [documentSource, setDocumentSource] = React.useState<string>();
  const [lookupLocation, setLookupLocation] = React.useState('');

  React.useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedName(name.trim()), 350);
    return () => window.clearTimeout(timeout);
  }, [name]);

  const shouldCheckForDuplicates =
    open && canSave && !initial && debouncedName.length >= DUPLICATE_CHECK_MIN_LENGTH;
  const duplicateSearchParams = {
    search: shouldCheckForDuplicates ? debouncedName : undefined,
    includeTestData: true,
    limit: 5
  } as const;
  const duplicateQuery = useListAccounts(duplicateSearchParams, {
    query: {
      queryKey: [...getListAccountsQueryKey(duplicateSearchParams), ownerKey],
      enabled: shouldCheckForDuplicates,
      staleTime: 30_000
    }
  });
  const duplicateCandidates = shouldCheckForDuplicates ? (duplicateQuery.data?.items ?? []) : [];
  const normalizedName = normalizeAccountName(name);
  const exactDuplicate = duplicateCandidates.find(
    (account) => normalizeAccountName(account.name) === normalizedName
  );

  React.useEffect(() => {
    if (!open || initialized.current === (initial?.id ?? 'new')) return;
    initialized.current = initial?.id ?? 'new';
    snapshotReady.current = false;
    const retained = draft?.current?.ownerKey === ownerKey ? draft.current : undefined;
    const suggested = retained?.values ?? prefill;
    if ((suggested && !initial) || retained?.dirty) formState.markDirty();
    setDocumentSource(retained?.source ?? (!initial ? prefillSource : undefined));
    setLookupLocation(retained?.location ?? '');
    setName(initial?.name ?? suggested?.name ?? '');
    setAccountKind(initial?.accountKind ?? suggested?.accountKind ?? defaultAccountKind);
    setRelationshipType(initial?.relationshipType ?? suggested?.relationshipType ?? defaultRelationshipType);
    setWebsite(initial?.website ?? suggested?.website ?? '');
    setIndustry(initial?.industry ?? suggested?.industry ?? '');
    setPhone(initial?.phone ?? suggested?.phone ?? '');
    setEmail(initial?.email ?? suggested?.email ?? '');
    setEmailDomain(initial?.emailDomain ?? suggested?.emailDomain ?? '');
    setStatus(initial?.status ?? retained?.values.status ?? '');
    setOwnerName(initial?.ownerName ?? retained?.values.ownerName ?? '');
    setAddressLine1(initial?.addressLine1 ?? suggested?.addressLine1 ?? '');
    setCity(initial?.city ?? suggested?.city ?? '');
    setStateProvince(initial?.stateProvince ?? suggested?.stateProvince ?? '');
    setPostcode(initial?.postcode ?? suggested?.postcode ?? '');
    setCountry(initial?.country ?? suggested?.country ?? '');
    const revenue = initial?.annualRevenue ?? suggested?.annualRevenue;
    setAnnualRevenue(revenue != null ? String(revenue) : '');
    setNumberOfEmployees(
      (initial?.numberOfEmployees ?? suggested?.numberOfEmployees) != null ? String(initial?.numberOfEmployees ?? suggested?.numberOfEmployees) : ''
    );
    setNotes(initial?.notes ?? suggested?.notes ?? '');
    setEmailConsent(initial?.emailConsent ?? retained?.values.emailConsent ?? 'unknown');
    setSmsConsent(initial?.smsConsent ?? retained?.values.smsConsent ?? 'unknown');
    setConsentSource(initial?.consentSource ?? retained?.values.consentSource ?? '');
    setInvestorType(initial?.investorType ?? retained?.values.investorType ?? '');
    setInvestmentFocus(initial?.investmentFocus ?? retained?.values.investmentFocus ?? '');
    const minimum = initial?.typicalChequeMin ?? retained?.values.typicalChequeMin;
    const maximum = initial?.typicalChequeMax ?? retained?.values.typicalChequeMax;
    setTypicalChequeMin(minimum != null ? String(minimum) : '');
    setTypicalChequeMax(maximum != null ? String(maximum) : '');
    setAccreditationStatus(initial?.accreditationStatus ?? retained?.values.accreditationStatus ?? '');
    setIsTestData(initial?.isTestData ?? retained?.values.isTestData ?? false);
    setError(null);
    setFieldIssues([]);
    setDebouncedName('');
  }, [open, initial, prefill, defaultAccountKind, defaultRelationshipType]);

  React.useLayoutEffect(() => {
    if (!open || presentation !== 'embedded') return;
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.getElementById('account-name')?.focus();
    return () => { const opener = returnFocus.current; queueMicrotask(() => { if (opener?.isConnected) opener.focus(); }); };
  }, [open, presentation]);

  function currentValues() {
      const supported: ReviewTenderPreparationAccountBody = {
        name: name.trim(),
        accountKind: relationshipType === 'b2c_consumer' ? 'person' : accountKind,
        relationshipType,
        website: website.trim() || null,
        industry: industry.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
        emailDomain: emailDomain.trim() || null,
        addressLine1: addressLine1.trim() || null,
        city: city.trim() || null,
        stateProvince: stateProvince.trim() || null,
        postcode: postcode.trim() || null,
        country: country.trim() || null,
        annualRevenue: annualRevenue === '' ? null : Number(annualRevenue),
        numberOfEmployees: numberOfEmployees === '' ? null : Number(numberOfEmployees),
        notes: notes.trim() || null
      };
      const values: AccountCreate = {
        ...supported,
        status: status.trim() || null,
        ownerName: ownerName.trim() || null,
        emailConsent,
        smsConsent,
        consentSource: consentSource.trim() || null,
        consentUpdatedAt: emailConsent !== 'unknown' || smsConsent !== 'unknown' ? new Date().toISOString() : null,
        investorType: investorType.trim() || null,
        investmentFocus: investmentFocus.trim() || null,
        typicalChequeMin: typicalChequeMin === '' ? null : Number(typicalChequeMin),
        typicalChequeMax: typicalChequeMax === '' ? null : Number(typicalChequeMax),
        accreditationStatus: accreditationStatus.trim() || null,
        isTestData
      };
      return { supported, values };
  }
  React.useEffect(() => {
    if (!snapshotReady.current) { snapshotReady.current = true; return; }
    if (open && draft && initialized.current) draft.current = { ownerKey, values: currentValues().values, source: documentSource, location: lookupLocation, dirty: formState.dirty };
  });
  const issues = React.useMemo(() => [...fieldIssues, ...(error ? [{ message: error }] : [])], [error, fieldIssues]);
  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault(); event.stopPropagation();
    if (!canSave || !name.trim() || exactDuplicate) return;
    setError(null); setFieldIssues([]);
    await formState.submit(async () => {
      const { supported, values } = currentValues();
      // Keep the generated validation bundle off the account editor's initial load, as in prompt transfer.
      const { createAccountBody, reviewTenderPreparationAccountBody } = await import('@cdf/api-zod');
      const validation = submission ? reviewTenderPreparationAccountBody.safeParse(supported) : createAccountBody.safeParse(values);
      if (!validation.success) throw new Error(validation.error.issues.map(issue => issue.path.join('.') + ': ' + issue.message).join('; '));
      if (submission) await submission.onReview(supported);
      else await onSubmit(values);
      initialized.current = null;
      if (draft) draft.current = undefined;
    }, err => setError(err instanceof Error ? err.message : 'Save failed. Please try again.'));
  }

  const content = <form aria-label={initial ? 'Edit account' : 'New account'} aria-busy={formState.pending}
          onKeyDown={event => { if (presentation === 'embedded' && event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); formState.requestClose(); } }}
          onChangeCapture={event => {
            formState.markDirty();
            if (event.target instanceof HTMLInputElement) {
              const id = event.target.id;
              setFieldIssues(current => current.filter(issue => issue.fieldId !== id));
            }
          }}
          onInvalidCapture={event => {
            if (!(event.target instanceof HTMLInputElement) || !event.target.id) return;
            event.preventDefault();
            const control = event.target;
            setFieldIssues(current => [...current.filter(issue => issue.fieldId !== control.id),
              { fieldId: control.id, message: control.validationMessage }]);
          }}
          onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
          <DialogHeader>
            {presentation === 'embedded' ? <><h3 className="text-lg font-semibold">New account</h3><p className="text-sm text-muted-foreground">Enter account details, then review before continuing.</p></> : <>
              <DialogTitle>{initial ? 'Edit account' : 'New account'}</DialogTitle>
              <DialogDescription>{initial ? 'Update the account details.' : 'Add an account to the Partner Engagement directory.'}</DialogDescription>
            </>}
          </DialogHeader>

          {!canSave && <p role="status">You cannot save account changes with your current access. Your draft remains available.</p>}
          {documentSource && <p className="rounded-md border p-3 text-sm">Suggested from {documentSource}. Check and edit these details before saving.</p>}
          <fieldset disabled={formState.pending || !canSave} className="contents">
          <div className="grid gap-1.5">
            <Label htmlFor="account-name">Name</Label>
            <Input
              id="account-name"
              aria-invalid={fieldIssues.some(issue => issue.fieldId === 'account-name') || undefined}
              aria-describedby={fieldIssues.some(issue => issue.fieldId === 'account-name') ? 'account-name-error' : undefined}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={200}
            />
            {fieldIssues.some(issue => issue.fieldId === 'account-name') && <p id="account-name-error" className="text-sm text-destructive">{fieldIssues.find(issue => issue.fieldId === 'account-name')?.message}</p>}
            {!initial && name.trim().length >= DUPLICATE_CHECK_MIN_LENGTH ? (
              <div aria-live="polite" className="min-h-5">
                {duplicateQuery.isFetching ? (
                  <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
                    <Loader2Icon aria-hidden className="size-3 animate-spin" />
                    Checking for an existing account…
                  </p>
                ) : exactDuplicate ? (
                  <div role="alert" className="space-y-2 rounded-md border p-3 text-sm">
                    <div className="flex gap-2">
                      <AlertTriangleIcon aria-hidden className="mt-0.5 size-4 shrink-0" />
                      <div>
                        <p className="font-medium">This account already exists</p>
                        <p className="text-muted-foreground text-xs">
                          Open {exactDuplicate.name} instead of creating a duplicate record.
                        </p>
                      </div>
                    </div>
                    <Button asChild size="sm" variant="outline">
                      <Link
                        to={`/partner-engagement/accounts/${exactDuplicate.id}`}
                        onClick={event => { event.preventDefault(); if (!formState.pending && canSave) navigate(`/partner-engagement/accounts/${exactDuplicate.id}`, () => { onOpenChange(false); return true; }); }}
                      >
                        Open existing account
                      </Link>
                    </Button>
                  </div>
                ) : duplicateCandidates.length > 0 ? (
                  <div className="rounded-md border border-dashed p-3 text-sm">
                    <p className="font-medium">Possible existing account</p>
                    <p className="text-muted-foreground mb-2 text-xs">
                      Check these similar names before creating a new record.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {duplicateCandidates.slice(0, 3).map((account) => (
                        <Button key={account.id} asChild size="sm" variant="outline">
                          <Link
                            to={`/partner-engagement/accounts/${account.id}`}
                            onClick={event => { event.preventDefault(); if (!formState.pending && canSave) navigate(`/partner-engagement/accounts/${account.id}`, () => { onOpenChange(false); return true; }); }}
                          >
                            Open {account.name}
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </div>
                ) : duplicateQuery.isError ? (
                  <p className="text-destructive text-xs">
                    Existing accounts could not be checked. Try again before saving.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="account-website">Website</Label>
            <Input id="account-website" value={website} onChange={event => setWebsite(event.target.value)} placeholder="https://…" maxLength={300} />
          </div>
          {!initial && accountKind === 'organization' && relationshipType !== 'b2c_consumer' && <AccountOrganisationReview
            open={open} allowed={canLookup} disabled={formState.pending || !canSave} ownerKey={ownerKey}
            formKey={submission?.preparationId ?? 'new'} tenantId={tenantId} entityId={entityId} values={currentValues().supported}
            onApply={values => {
              formState.markDirty();
              if (values.industry !== undefined) setIndustry(values.industry ?? '');
              if (values.phone !== undefined) setPhone(values.phone ?? '');
              if (values.email !== undefined) setEmail(values.email ?? '');
              if (values.addressLine1 !== undefined) setAddressLine1(values.addressLine1 ?? '');
              if (values.city !== undefined) setCity(values.city ?? '');
              if (values.stateProvince !== undefined) setStateProvince(values.stateProvince ?? '');
              if (values.postcode !== undefined) setPostcode(values.postcode ?? '');
              if (values.country !== undefined) setCountry(values.country ?? '');
              if (values.notes !== undefined) setNotes(values.notes ?? '');
            }} />}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5"><Label htmlFor="account-kind">Account kind</Label><Select value={accountKind} onValueChange={(value) => { formState.markDirty(); setAccountKind(value as AccountKind); }}><SelectTrigger id="account-kind"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="organization">Organization</SelectItem><SelectItem value="person">Person</SelectItem></SelectContent></Select></div>
            <div className="grid gap-1.5"><Label htmlFor="account-relationship">Relationship</Label><Select value={relationshipType} onValueChange={(value) => { formState.markDirty(); const next = value as AccountRelationshipType; setRelationshipType(next); if (next === 'b2c_consumer') setAccountKind('person'); }}><SelectTrigger id="account-relationship"><SelectValue /></SelectTrigger><SelectContent>{ACCOUNT_RELATIONSHIP_OPTIONS.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
          {!initial && accountKind === 'organization' && relationshipType !== 'b2c_consumer' && <AccountBusinessLookup
            open={open} ownerKey={ownerKey} tenantId={tenantId} entityId={entityId}
            allowed={canLookup} disabled={formState.pending || !canSave}
            name={name} website={website} notes={notes} location={lookupLocation} onLocationChange={setLookupLocation} onApply={values => {
              formState.markDirty();
              if (values.website !== undefined) setWebsite(values.website);
              if (values.notes !== undefined) setNotes(values.notes);
            }} />}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="account-industry">Industry</Label>
              <Input
                id="account-industry"
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                maxLength={100}
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="account-phone">Phone</Label>
            <Input
              id="account-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={50}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="account-email">Email</Label>
              <Input id="account-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="account-email-domain">Email domain</Label>
              <Input id="account-email-domain" value={emailDomain} onChange={(e) => setEmailDomain(e.target.value)} placeholder="example.com" />
            </div>
          </div>
          {!submission && <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="account-status">Status</Label>
              <Input id="account-status" value={status} onChange={(e) => setStatus(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="account-owner">Owner</Label>
              <Input id="account-owner" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
            </div>
          </div>}
          <div className="grid gap-1.5">
            <Label htmlFor="account-address">Address</Label>
            <Input id="account-address" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-4">
            <Input aria-label="City" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
            <Input aria-label="State or province" value={stateProvince} onChange={(e) => setStateProvince(e.target.value)} placeholder="State" />
            <Input aria-label="Postcode" value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Postcode" />
            <Input aria-label="Country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="account-revenue">Annual revenue</Label>
              <Input id="account-revenue" type="number" min={0} step="0.01" value={annualRevenue} onChange={(e) => setAnnualRevenue(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="account-employees">Employees</Label>
              <Input id="account-employees" type="number" min={0} step={1} value={numberOfEmployees} onChange={(e) => setNumberOfEmployees(e.target.value)} />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="account-notes">Notes</Label>
            <Textarea
              id="account-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={5000}
            />
          </div>
          {!submission && relationshipType === 'b2c_consumer' && <div className="rounded-md border p-3"><p className="mb-3 text-sm font-medium">Consent management</p><div className="grid gap-3 sm:grid-cols-3"><div className="grid gap-1.5"><Label>Email</Label><Select value={emailConsent} onValueChange={(value) => { formState.markDirty(); setEmailConsent(value as ConsentStatus); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="unknown">Unknown</SelectItem><SelectItem value="opted_in">Opted in</SelectItem><SelectItem value="opted_out">Opted out</SelectItem></SelectContent></Select></div><div className="grid gap-1.5"><Label>SMS</Label><Select value={smsConsent} onValueChange={(value) => { formState.markDirty(); setSmsConsent(value as ConsentStatus); }}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="unknown">Unknown</SelectItem><SelectItem value="opted_in">Opted in</SelectItem><SelectItem value="opted_out">Opted out</SelectItem></SelectContent></Select></div><div className="grid gap-1.5"><Label htmlFor="consent-source">Source</Label><Input id="consent-source" value={consentSource} onChange={(event) => setConsentSource(event.target.value)} placeholder="Web form, phone…" /></div></div></div>}
          {!submission && relationshipType === 'investor' && <div className="rounded-md border p-3"><p className="mb-3 text-sm font-medium">Investor profile</p><div className="grid gap-3 sm:grid-cols-2"><Input aria-label="Investor type" value={investorType} onChange={(event) => setInvestorType(event.target.value)} placeholder="VC, angel, family office…" /><Input aria-label="Accreditation status" value={accreditationStatus} onChange={(event) => setAccreditationStatus(event.target.value)} placeholder="Accreditation status" /><Input aria-label="Investment focus" value={investmentFocus} onChange={(event) => setInvestmentFocus(event.target.value)} placeholder="Investment focus" /><div className="grid grid-cols-2 gap-2"><Input aria-label="Minimum cheque" type="number" min={0} value={typicalChequeMin} onChange={(event) => setTypicalChequeMin(event.target.value)} placeholder="Min cheque" /><Input aria-label="Maximum cheque" type="number" min={0} value={typicalChequeMax} onChange={(event) => setTypicalChequeMax(event.target.value)} placeholder="Max cheque" /></div></div></div>}
          {!submission && <TestDataStatusControl checked={isTestData} onCheckedChange={value => { formState.markDirty(); setIsTestData(value); }} />}

          </fieldset>
          <FormFeedback issues={issues} pending={formState.pending} />

          <DialogFooter>
            {draft && <Button type="button" disabled={formState.pending} variant="outline" onClick={formState.requestClose}>Back to tender (keep account draft)</Button>}
            <Button type="button" disabled={formState.pending} variant="outline" onClick={() => draft ? formState.requestClose() : formState.discard(() => { initialized.current = null; })}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSave || formState.pending || name.trim().length === 0 || Boolean(exactDuplicate)}
            >
              {formState.pending ? (submission ? 'Preparing review…' : 'Saving…') : submission ? 'Review account details' : initial ? 'Save changes' : 'Create account'}
            </Button>
          </DialogFooter>
        </form>;
  if (presentation === 'embedded') return open ? content : null;
  return <Dialog open={open} onOpenChange={next => {
    if (!next) { if (!formState.dirty) initialized.current = null; formState.requestClose(); }
  }}><DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl"
    onOpenAutoFocus={() => { if (document.activeElement instanceof HTMLElement && document.activeElement !== document.body) returnFocus.current = document.activeElement; }}
    onCloseAutoFocus={event => { if (returnFocus.current?.isConnected) { event.preventDefault(); returnFocus.current.focus(); } }}>
    {content}
  </DialogContent></Dialog>;
}
